import React from 'react';

// External dependencies
import { HandWave, Memo, KickOut, KickOutVisitor } from 'kits/IconKit2';
import IvicosStrings from 'kits/language/stringKit';

// Shared state
import { useLocalProfile, useOtherUsersStatus, useSetOtherUsersStatusFromServer } from 'shared-state/identity/hooks';
import { IProfile } from 'shared-state/identity/types';
import { useCurrentRoomId } from 'shared-state/location/hook';
import { useMessageComposerUserIds } from 'shared-state/messaging/hooks';
import { makeMessageId, useReceivedMessageInbox } from 'shared-state/notifications/hooks';
import { useControlNameWidthInMemo } from 'shared-state/display-options/hooks';

// Services
import { useEmittingEvents } from 'services/socket-connection/socket-adapters/event-actions/emittingEvents';

// Local imports
import { IUserAction } from './userTypes';
import { redirectUserModal } from './redirectionModal/redirectionModal';

const USER_TYPES = {
    VISITOR: 'visitor',
    AUTO_CONFIRMED_VISITOR: 'auto-confirmed-visitor',
    USER: 'user'
} as const;

export const useUserActions = (user: IProfile): IUserAction[] => {
    const [currentRoomId] = useCurrentRoomId();
    const localProfile = useLocalProfile();
    const [, setMessageComposerUserIds] = useMessageComposerUserIds();
    const [, addMessage] = useReceivedMessageInbox();
    const { inviteUserToRoom, kickVisitor, redirectUserToPersonalRoom, knock } = useEmittingEvents();
    const [, setControlNameWidthInMemo] = useControlNameWidthInMemo();
    const chosenLang = localStorage.getItem('chosenLanguage') || 'de';
    const [otherUsersStatusFromServer] = useSetOtherUsersStatusFromServer();
    const otherUsersStatus = useOtherUsersStatus(user.uid) || otherUsersStatusFromServer?.find((status) => status.userId === user.uid);

    // Early return with empty array if any required data is missing
    if (!localProfile?.uid || !user?.uid) {
        console.warn('useUserActions: Missing required profile data');
        return [];
    }

    // Prevent self-actions
    if (localProfile.uid === user.uid) {
        return [];
    }

    // Validate room data
    if (!localProfile.room || !user.room) {
        console.warn('useUserActions: Missing room data');
        return [];
    }

    const sendMemoAction: IUserAction = {
        key: 'send-memo',
        title: IvicosStrings.sendUserMemoActionTitle,
        action: () => {
            try {
                setMessageComposerUserIds([user.uid]);
                setControlNameWidthInMemo(true);
            } catch (error) {
                console.error('Failed to send memo:', error);
            }
        },
        onRenderIcon: () => <Memo />
    };

    const inviteToRoomAction: IUserAction = {
        key: 'invite-user',
        title: IvicosStrings.inviteUserToRoomActionTitle,
        action: () => {
            if (!currentRoomId) {
                console.warn('useUserActions: No current room ID available');
                return;
            }

            try {
                inviteUserToRoom(user.uid, currentRoomId);
                addMessage({
                    id: makeMessageId(),
                    type: 'confirmation',
                    body: IvicosStrings.invitationSentNotificationActionTitle,
                    timeout: 2000,
                    sender: '',
                    meta: {},
                    timestamp: new Date()
                });
            } catch (error) {
                console.error('Failed to invite user:', error);
            }
        },
        onRenderIcon: () => <HandWave />
    };

    const customTitleForRedirectUserAction = (): string => {
        const isVisitor = user.type === USER_TYPES.VISITOR || user.type === USER_TYPES.AUTO_CONFIRMED_VISITOR;
        const type = chosenLang === 'de' ? (isVisitor ? 'Besucher' : 'Benutzer') : isVisitor ? 'Visitor' : 'User';
        return IvicosStrings.redirectUserActionTitle.replace('{userOrVisitor}', type);
    };

    const redirectUserToPersonalRoomAction: IUserAction = {
        key: 'redirect-user-to-personal-room',
        title: customTitleForRedirectUserAction(),
        action: () => {
            try {
                redirectUserModal(user, redirectUserToPersonalRoom);
            } catch (error) {
                console.error('Failed to redirect user:', error);
            }
        },
        onRenderIcon: () => <KickOut />
    };

    const kickOutVisitorAction: IUserAction = {
        key: 'kick-out-visitor',
        title: IvicosStrings.kickOutVisitorActionTitle,
        action: () => {
            try {
                kickVisitor(user.uid);
            } catch (error) {
                console.error('Failed to kick visitor:', error);
            }
        },
        onRenderIcon: () => <KickOutVisitor />
    };

    let actions: IUserAction[] = [sendMemoAction];

    const isInSameRoom = localProfile.room === user.room;
    const isPersonalRoomOwner = localProfile.room.includes('personal') && localProfile.room.includes(localProfile.uid);
    const isVisitorBeingHovered = [USER_TYPES.VISITOR, USER_TYPES.AUTO_CONFIRMED_VISITOR].includes(user.type as any);
    const isUserHovering = localProfile.type === USER_TYPES.USER;

    if (!isInSameRoom) {
        actions = [...actions, inviteToRoomAction];
    }

    if (isPersonalRoomOwner && isInSameRoom) {
        actions = [...actions, redirectUserToPersonalRoomAction];
    }

    if (isVisitorBeingHovered && isUserHovering) {
        actions = [...actions, kickOutVisitorAction];
    }

    if (otherUsersStatus && otherUsersStatus.emergencyContact) {
        const { status, endTime, returnDate, emergencyContact } = otherUsersStatus;
        const busyEmergencyContact = emergencyContact as { inviteToRoom?: boolean; knockDoor?: boolean; textMessage?: boolean };
        const oooEmergencyContact = emergencyContact as { email?: boolean; chat?: boolean };

        if (status === 'busy' && endTime && endTime > Date.now()) {
            const inviteToRoomEnabled = busyEmergencyContact.inviteToRoom ?? busyEmergencyContact.knockDoor;
            const textMessage = !!busyEmergencyContact.textMessage;
            if (inviteToRoomEnabled && textMessage) {
                actions = [sendMemoAction, inviteToRoomAction];
            } else if (!inviteToRoomEnabled && !textMessage) {
                actions = [];
            } else if (inviteToRoomEnabled) {
                actions = [inviteToRoomAction];
            } else {
                actions = [sendMemoAction];
            }
        }

        if (status === 'out-of-office' && returnDate) {
            const email = !!oooEmergencyContact.email;
            const chat = !!oooEmergencyContact.chat;
            if (email && chat) {
                actions = [sendMemoAction];
            } else if (email) {
                actions = [];
            } else {
                actions = [sendMemoAction];
            }
        }
    }

    return actions;
};
